import { X } from 'lucide-react';
import { Link } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';

interface HowItWorksModalProps {
  isOpen: boolean;
  onClose: (value: boolean) => void;
}

const HowItWorksModal: React.FC<HowItWorksModalProps> = ({ isOpen, onClose }) => {
  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[999] flex items-center justify-center bg-black/80 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-[1000] w-[90vw] max-w-screen-xl -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-3xl border border-[#282133] bg-[#10091C] outline-none">
          <Dialog.Title className="sr-only">How It Works</Dialog.Title>
          <Dialog.Description className="sr-only">Learn how to use the Token Racer platform</Dialog.Description>

          <div className="w-full max-w-7xl rounded-lg border border-gray-800 bg-gray-900 shadow-xl">
            <div className="sticky top-0 z-30 flex items-center justify-between border-b border-gray-800 bg-gray-900 p-4">
              <h2 className="text-xl font-bold text-white">How does it work?</h2>
              <Dialog.Close className="bg-transparent text-white transition-colors">
                <X size={24} />
              </Dialog.Close>
            </div>

            <div className="lg:ites grid max-h-[90vh] items-start gap-6 overflow-y-auto p-4 lg:grid-cols-2">
              <div className="shrink-0 lg:sticky lg:top-1/2 lg:-translate-y-1/2">
                <video autoPlay muted loop={false} className="h-full w-full object-center" controls>
                  <source
                    src="https://ithaca-images.s3.eu-west-1.amazonaws.com/TokenRacerGuide-reduced.mp4"
                    type="video/mp4"
                  />
                </video>
              </div>

              <div className="space-y-6 p-6 pl-0">
                <div className="flex gap-6">
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-purple-600 font-bold text-white">
                    1
                  </div>
                  <div className="space-y-2">
                    <Link to="/race-wizard" className="flex cursor-pointer items-center text-lg font-bold text-white">
                      Race Wizard
                    </Link>
                    <p className="leading-relaxed text-gray-300">
                      Create your own race by selecting tokens you want to watch competing and by setting the race
                      duration. Customize the staking reward pool.
                    </p>
                  </div>
                </div>

                <div className="flex gap-6">
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-purple-600 font-bold text-white">
                    2
                  </div>
                  <div className="space-y-2">
                    <Link to="/join-races" className="flex cursor-pointer items-center text-lg font-bold text-white">
                      Join Races
                    </Link>
                    <p className="leading-relaxed text-gray-300">
                      Participate in any race that anyone may have created before it commences by staking on your
                      preferred outcome. Staking reward multipliers are updated in real-time.
                    </p>
                  </div>
                </div>

                <div className="flex gap-6">
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-purple-600 font-bold text-white">
                    3
                  </div>
                  <div className="space-y-2">
                    <Link to="/watch" className="flex cursor-pointer items-center text-lg font-bold text-white">
                      Watch
                    </Link>
                    <p className="leading-relaxed text-gray-300">
                      Follow the action as the race unfolds. Engage with a vibrant community, cheer for your favorite
                      token, and stay updated with market trends.
                    </p>
                  </div>
                </div>

                <div className="flex gap-6">
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-purple-600 font-bold text-white">
                    4
                  </div>
                  <div className="space-y-2">
                    <h3 className="flex items-center text-lg font-bold text-white">Fair On-Chain Resolution</h3>
                    <p className="leading-relaxed text-gray-300">
                      Races are resolved using price data from CoinGecko, delivered on-chain through the Gelato network.
                      This ensures a tamper-proof outcome. The winning token is determined by the highest percentage
                      performance for the race duration (or the highest adjusted performance based on volatility and
                      FDV).
                    </p>
                  </div>
                </div>

                <div className="flex gap-6">
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-purple-600 font-bold text-white">
                    5
                  </div>
                  <div className="space-y-2">
                    <h3 className="flex items-center text-lg font-bold text-white">Rewards</h3>
                    <p className="leading-relaxed text-gray-300">
                      All payouts are executed via audited smart contracts, ensuring fairness and security.
                    </p>
                  </div>
                </div>

                <div className="flex gap-6">
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-purple-600 font-bold text-white">
                    6
                  </div>
                  <div className="space-y-2">
                    <h3 className="flex items-center text-lg font-bold text-white">Transparent & Secure</h3>
                    <p className="leading-relaxed text-gray-300">
                      Every detail—from stakes and outcomes to payouts—is recorded on the Binance Smart Chain,
                      guaranteeing transparency and security.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default HowItWorksModal;
